import React, { useState, useEffect } from 'react';
import { Check, X, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { iconService, IconResult } from '../../services/iconService';
import { cn } from '../../lib/utils';

interface IconPreviewPanelProps {
  icon: IconResult;
  onConfirm: (icon: IconResult, color: string, size: number) => void;
  onCancel: () => void;
  initialColor?: string;
  initialSize?: number;
}

// Common color swatches
const COLOR_SWATCHES = [
  { name: 'White', hex: 'ffffff' },
  { name: 'Black', hex: '000000' },
  { name: 'Gray', hex: '6b7280' },
  { name: 'Red', hex: 'ef4444' },
  { name: 'Orange', hex: 'f97316' },
  { name: 'Yellow', hex: 'eab308' },
  { name: 'Green', hex: '22c55e' },
  { name: 'Teal', hex: '14b8a6' },
  { name: 'Blue', hex: '3b82f6' },
  { name: 'Indigo', hex: '6366f1' },
  { name: 'Purple', hex: '8b5cf6' },
  { name: 'Pink', hex: 'ec4899' },
];

// Common size options
const SIZE_OPTIONS = [16, 24, 32, 48, 64, 96, 128];

export const IconPreviewPanel: React.FC<IconPreviewPanelProps> = ({
  icon,
  onConfirm,
  onCancel,
  initialColor = '000000',
  initialSize = 64,
}) => {
  const [color, setColor] = useState(initialColor);
  const [size, setSize] = useState(initialSize);
  const [colorInput, setColorInput] = useState(initialColor);

  // Generate preview URL
  const previewUrl = iconService.getIconUrl(icon.id, color, size);

  // Update color input when color changes
  useEffect(() => {
    setColorInput(color);
  }, [color]);

  // Handle color input change with validation
  const handleColorInputChange = (value: string) => {
    // Remove # if present
    const cleanValue = value.replace('#', '');
    setColorInput(cleanValue);

    // Validate and apply
    if (/^[0-9a-fA-F]{3,6}$/.test(cleanValue)) {
      setColor(cleanValue);
    }
  };

  // Handle color input blur - expand 3-char hex to 6-char
  const handleColorInputBlur = () => {
    let normalizedColor = colorInput;

    // Expand 3-char hex (e.g., "fff" -> "ffffff")
    if (/^[0-9a-fA-F]{3}$/.test(colorInput)) {
      normalizedColor = colorInput
        .split('')
        .map((c) => c + c)
        .join('');
    }

    if (/^[0-9a-fA-F]{6}$/.test(normalizedColor)) {
      setColor(normalizedColor);
      setColorInput(normalizedColor);
    } else {
      // Reset to current valid color
      setColorInput(color);
    }
  };

  // Reset to defaults
  const handleReset = () => {
    setColor(initialColor);
    setSize(initialSize);
    setColorInput(initialColor);
  };

  // Confirm selection
  const handleConfirm = () => {
    onConfirm(icon, color, size);
  };

  return (
    <div className="flex flex-col h-full bg-background border-l border-border">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div>
          <h3 className="text-sm font-medium">Customize Icon</h3>
          <p className="text-xs text-muted-foreground">{icon.name} • {icon.collectionName}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Preview */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div
          className="p-6 rounded-lg border-2 border-dashed border-border flex items-center justify-center"
          style={{
            backgroundColor: isLightColor(color) ? '#1a1a1a' : '#f5f5f5',
          }}
        >
          <img
            src={previewUrl}
            alt={icon.name}
            style={{ width: size, height: size }}
            className="object-contain"
          />
        </div>
      </div>

      {/* Controls */}
      <div className="px-4 py-3 border-t border-border space-y-4">
        {/* Color Control */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Color</Label>
            <div className="flex items-center gap-1">
              <span className="text-xs text-muted-foreground">#</span>
              <Input
                value={colorInput}
                onChange={(e) => handleColorInputChange(e.target.value)}
                onBlur={handleColorInputBlur}
                className="w-20 h-6 text-xs font-mono px-1"
                maxLength={6}
              />
              <div
                className="w-6 h-6 rounded border border-border"
                style={{ backgroundColor: `#${color}` }}
              />
            </div>
          </div>

          {/* Color Swatches */}
          <div className="flex flex-wrap gap-1">
            {COLOR_SWATCHES.map((swatch) => (
              <button
                key={swatch.hex}
                onClick={() => setColor(swatch.hex)}
                className={cn(
                  "w-5 h-5 rounded-sm transition-all",
                  color === swatch.hex
                    ? "ring-2 ring-primary ring-offset-1"
                    : "hover:scale-110",
                  swatch.hex === 'ffffff' && "border border-border"
                )}
                style={{ backgroundColor: `#${swatch.hex}` }}
                title={swatch.name}
              />
            ))}
          </div>
        </div>

        {/* Size Control */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Size</Label>
            <span className="text-xs text-muted-foreground">{size}px</span>
          </div>

          {/* Size Options */}
          <div className="flex flex-wrap gap-1">
            {SIZE_OPTIONS.map((s) => (
              <button
                key={s}
                onClick={() => setSize(s)}
                className={cn(
                  "px-2 py-1 text-[10px] rounded transition-colors",
                  size === s
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted hover:bg-muted/80"
                )}
              >
                {s}px
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/30">
        <Button variant="ghost" size="sm" onClick={handleReset}>
          <RotateCcw className="h-3.5 w-3.5 mr-1" />
          Reset
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleConfirm}>
            <Check className="h-3.5 w-3.5 mr-1" />
            Use Icon
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Helper to determine if a color is light (for contrast background)
 */
function isLightColor(hex: string): boolean {
  // Expand 3-char hex
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((c) => c + c)
      .join('');
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate perceived brightness (YIQ formula)
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;

  return brightness > 128;
}

export default IconPreviewPanel;
